import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Snapshot of the last-known signed-in user. Lets the app render its shell
// instantly on refresh instead of blocking behind /auth/me.
const CACHED_USER_KEY = "cachedUser";
function readCachedUser() {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const initialUser = typeof window !== "undefined" && localStorage.getItem("token") ? readCachedUser() : null;
  const [user, setUser] = useState(initialUser);
  // If we already have a cached user we can render immediately and revalidate
  // in the background. Only block ("loading") when there's a token but no cache.
  const [loading, setLoading] = useState(!!localStorage.getItem("token") && !initialUser);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(data));
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem(CACHED_USER_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem(CACHED_USER_KEY);
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refetch: fetchMe }),
    [user, loading, login, logout, fetchMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
